import {
  Body,
  Caption,
  EmptyState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  VBadge,
  VButton,
  cn,
} from '@vrzl/ui';
import { CheckCircle2, CircleOff, FileCheck2, Search } from 'lucide-react';

import { perspectiveColor } from '@/domains/perspective/perspectiveColor';

import { useCriteria } from '../api/queries';
import type { CriterionListItem } from '../api/types';
import { CRITERIA_SEARCH_EMPTY_TITLE } from '../helpers/criteriaSearch';
import { countActiveCriteria } from '../helpers/criteriaSelection';
import { useCriteriaSearch } from '../hooks/useCriteriaSearch';

import { CriteriaSearch } from './criteriaSearch';

const getName = (criterion: CriterionListItem) => criterion.name;
const getValues = (criterion: CriterionListItem) => [criterion.code, criterion.description];

type CriteriaPanelProps = {
  applicationId: string;
  /** Het actieve perspectief van de gebruiker; alleen criteria hiervan worden getoond. */
  perspective: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inactiveCriterionIds: ReadonlySet<string>;
  onToggleCriterion: (id: string) => void;
  onValidate: () => void;
  isValidating: boolean;
};

export function CriteriaPanel({ applicationId, perspective, open, onOpenChange, inactiveCriterionIds, onToggleCriterion, onValidate, isValidating }: CriteriaPanelProps) {
  // Alleen criteria van het actieve perspectief ophalen (server filtert via PerspectiveCriterion).
  const { data: criteriaData } = useCriteria(applicationId, { perspectiveId: perspective.id });

  const criteria = criteriaData?.items ?? [];

  const {
    query,
    setQuery,
    results: filteredCriteria,
    isSearching,
  } = useCriteriaSearch(criteria, { getName, getValues });

  const activeCriteria = countActiveCriteria(criteria, inactiveCriterionIds);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader className="flex-row items-start justify-between gap-3 px-6 pt-6 pb-4 pr-12">
          <div className="flex flex-col gap-1.5">
            <SheetTitle className="text-lg">Criteria</SheetTitle>
            <SheetDescription>
              {activeCriteria} van {criteria.length} criteria actief
            </SheetDescription>
          </div>
          <VButton
            variant="primary"
            size="sm"
            icon={FileCheck2}
            disabled={activeCriteria === 0 || isValidating}
            onClick={onValidate}
          >
            {isValidating ? 'Bezig…' : 'Valideer'}
          </VButton>
        </SheetHeader>

        <div className="flex flex-col gap-8 px-6 pb-8">
          <CriteriaSearch value={query} onChange={setQuery} />

          {filteredCriteria.length === 0 ? (
            <EmptyState
              icon={Search}
              title={isSearching ? CRITERIA_SEARCH_EMPTY_TITLE : 'Geen criteria voor dit perspectief'}
            />
          ) : (
            <div>
              <div className="mb-3 flex items-center gap-2.5">
                <VBadge color={perspectiveColor[perspective.name] ?? 'neutral'}>
                  {perspective.name}
                </VBadge>
                <Caption as="span">
                  {filteredCriteria.filter((c) => !inactiveCriterionIds.has(c.id)).length} / {filteredCriteria.length} actief
                </Caption>
              </div>

              <div className="flex flex-col gap-2.5">
                {filteredCriteria.map((criterion) => {
                  const active = !inactiveCriterionIds.has(criterion.id);
                  return (
                    <button
                      key={criterion.id}
                      type="button"
                      onClick={() => onToggleCriterion(criterion.id)}
                      className={cn(
                        'flex w-full cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-left',
                        active
                          ? 'border-border bg-card'
                          : 'border-neutral-200 bg-neutral-200/30 opacity-50',
                      )}
                    >
                      {active ? (
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-600" />
                      ) : (
                        <CircleOff className="mt-0.5 size-4 shrink-0 text-neutral-400" />
                      )}
                      <div className="min-w-0">
                        <Body weight="medium">{criterion.name}</Body>
                        {criterion.description && (
                          <Caption className="mt-1 leading-relaxed">
                            {criterion.description}
                          </Caption>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
